import { Controller } from "@hotwired/stimulus";
import { post } from "@rails/request.js";

// Tells the server "someone is still looking at this board" so its realtime cards keep ticking. Not a
// data poll: the ping only refreshes a cache key, which is why it runs on a timer and is mounted once
// per grid rather than once per card. The ticker REPLACES each card's frame rather than re-requesting
// its src, so without this nothing re-hits the endpoint that seeded presence.
export default class extends Controller {
  static values = { url: String, filters: Object, intervalMs: Number };

  connect() {
    this.ping(); // the first tick is a whole interval away; claim presence immediately
    this.timer = setInterval(() => this.ping(), this.intervalMsValue);
    // Bound once and kept, so disconnect() removes the same reference it added.
    this.onVisibilityChange = () => {
      if (document.visibilityState === "visible") this.ping();
    };
    document.addEventListener("visibilitychange", this.onVisibilityChange);
  }

  disconnect() {
    clearInterval(this.timer);
    document.removeEventListener("visibilitychange", this.onVisibilityChange);
  }

  async ping() {
    // A BACKGROUNDED TAB IS NOBODY WATCHING. Skipping while hidden is what makes a forgotten tab stop
    // its board's ticker on its own, instead of leaving it running for as long as the browser is open.
    // Resuming is immediate rather than waiting out the interval: the visibilitychange handler above
    // pings the moment the tab comes back, so a returning viewer doesn't stare at a frozen board.
    if (document.visibilityState === "hidden") return;

    // A dropped ping is not worth reporting: presence outlives several intervals, and the next ping is
    // seconds away. Failing quietly beats a console full of noise every time the network hiccups.
    try {
      // ASKS FOR THE BADGE ONLY IF ONE IS ON THE PAGE. This controller is mounted by the shared card
      // grid, which the Home page's pinned boards also render -- without a badge. Asking anyway made the
      // server run a freshness read every few seconds to replace an element that was not there.
      //
      // responseKind turbo-stream so request.js applies the response: the badge's age and colour are
      // computed server-side and would otherwise sit frozen at whatever they were when the page loaded.
      const watermark = document.getElementById("live_watermark") ? "1" : null;
      await post(this.urlValue, {
        body: { filters: this.filtersValue, watermark },
        responseKind: "turbo-stream",
      });
    } catch {
      // Offline or mid-deploy. The board keeps showing its last data; the next ping resumes ticking.
    }
  }
}
